import React from "react";

import { SKSMediaChooserBlock as Value } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

import { PlaybackBehavior, VideoPlayerControls } from "../VideoPlayerControls";

/**
 * Allow setting of ImageChooserBlock props from outside of MediaChooserBlock
 * Partial so we only ever need to opt in to the things we want to change
 * Excluding things that are already set in some other way
 */
type ImageChooserProps = Partial<
    Omit<React.ComponentProps<typeof ImageChooserBlock>, "value" | "attrs">
>;

/** Same deal as ImageChooserProps, for VideoPlayerControls */
type VideoPlayerProps = Partial<
    Omit<
        React.ComponentProps<typeof VideoPlayerControls>,
        "value" | "playbackBehavior" | "wrapperAttrs"
    >
>;

interface Props {
    value: Value;
    /**
     * @deprecated Only intended to be used in 2024 design with legacy `media.type === "video"`
     * (FullWidthImageBlock is the only living example at the time of writing this).
     *
     * Do not pass this prop in new 2026 blocks. If it's omitted and a legacy video
     * is selected in the CMS, it'll default to "autoplay". If you want native playback
     * controls in the 2026 design, add it to `VideoPlaybackBehavior`.
     *
     * 2026 redesign retooled MediaChooserBlock to add `video_with_options` which bundled
     * the option into a spot where it belonged; previously that option was outside of
     * MediaChooserBlock in a way that required `type` to exist.
     */
    type?: null | ("background" | "foreground");
    wrapperAttrs?: React.HTMLAttributes<HTMLElement>;
    imageProps?: ImageChooserProps;
    videoProps?: VideoPlayerProps;
}

// Translate legacy type prop to playbackBehavior
const typeToPlaybackBehavior = (type: Props["type"]): PlaybackBehavior => {
    if (type === "foreground") return "native_controls";
    return "autoplay";
};

/** Width-over-height ratio of the selected media, if known */
export const getMediaAspectRatio = (value: Value): number | null => {
    if (!value || value.length === 0) return null;
    const media = value[0];
    switch (media.type) {
        case "image":
            return media.value.height > 0
                ? media.value.width / media.value.height
                : null;
        case "video":
            return media.value.aspectRatio;
        case "video_with_options":
            return media.value.video?.aspectRatio ?? null;
    }
    return assertNever(media);
};

export const MediaChooserBlock = ({
    value,
    type,
    wrapperAttrs,
    videoProps,
    imageProps,
}: Props) => {
    if (!value || value.length === 0) return <></>;
    const media = value[0];

    if (media.type === "video")
        return (
            <VideoPlayerControls
                value={media.value}
                playbackBehavior={typeToPlaybackBehavior(type)}
                wrapperAttrs={wrapperAttrs}
                {...videoProps}
            />
        );

    if (media.type === "video_with_options")
        return media.value.video ? (
            <VideoPlayerControls
                value={media.value.video}
                playbackBehavior={media.value.playback_behavior}
                wrapperAttrs={wrapperAttrs}
                {...videoProps}
            />
        ) : (
            <></>
        );

    if (media.type === "image")
        return (
            <ImageChooserBlock
                value={media.value}
                attrs={wrapperAttrs}
                {...imageProps}
            />
        );

    return <></>;
};
